import { apiClient, type QueryParams } from './client';

import type {
  DeleteGameResponse,
  GameAssetAction,
  GameMetadata,
  GameQueryParamsValues,
  GameRecord,
  GenerateGameAssetSignedUrlResponse,
  GetGameAssetHashesResponse,
  PurgeGameAssetCacheResponse,
  SetGameAssetContentHashResponse
} from '@/types/game';

export type { GameRecord, GameMetadata } from '@/types/game';

export interface ListGamesParams {
  onlyMyGames?: boolean;
  published?: GameQueryParamsValues;
}

export interface CreateGameParams {
  metadata: GameMetadata;
}

export interface ModifyGameParams {
  published?: boolean;
  metadata?: GameMetadata;
}

export interface GenerateSignedUrlParams {
  filePath: string;
  contentType: string;
  action: GameAssetAction;
}

export interface SetContentHashParams {
  filePath: string;
  contentHash: string;
}

export interface UpdateThumbnailParams {
  filename: string;
  contentType: string;
  base64EncodedImage: string;
}

export interface PurgeAssetCacheParams {
  paths: string[];
}

export const gamesApi = {
  list: (params?: ListGamesParams): Promise<GameRecord[]> =>
    apiClient.get<GameRecord[]>('/games', params as QueryParams),

  get: (gameId: string): Promise<GameRecord> =>
    apiClient.get<GameRecord>(`/games/${gameId}`),

  create: (params: CreateGameParams): Promise<GameRecord> =>
    apiClient.post<GameRecord>('/games', params),

  modify: (gameId: string, params: ModifyGameParams): Promise<GameRecord> =>
    apiClient.patch<GameRecord>(`/games/${gameId}`, params),

  delete: (gameId: string, deleteFiles = false): Promise<DeleteGameResponse> =>
    apiClient.delete<DeleteGameResponse>(`/games/${gameId}`, { deleteFiles }),

  generateSignedUrl: (
    gameId: string,
    params: GenerateSignedUrlParams
  ): Promise<GenerateGameAssetSignedUrlResponse> =>
    apiClient.post<GenerateGameAssetSignedUrlResponse>(
      `/games/${gameId}/assets/signed-url`,
      params
    ),

  getAssetHashes: (gameId: string): Promise<GetGameAssetHashesResponse> =>
    apiClient.get<GetGameAssetHashesResponse>(`/games/${gameId}/assets/hashes`),

  setAssetContentHash: (
    gameId: string,
    params: SetContentHashParams
  ): Promise<SetGameAssetContentHashResponse> =>
    apiClient.post<SetGameAssetContentHashResponse>(`/games/${gameId}/assets/hashes`, params),

  purgeAssetCache: (
    gameId: string,
    params: PurgeAssetCacheParams
  ): Promise<PurgeGameAssetCacheResponse> =>
    apiClient.post<PurgeGameAssetCacheResponse>(`/games/${gameId}/assets/purge-cache`, params),

  updateThumbnail: (gameId: string, params: UpdateThumbnailParams): Promise<GameRecord> =>
    apiClient.post<GameRecord>(`/games/${gameId}/thumbnail`, params)
};
